import clientErrorLogger from "./clientErrorLogger";
import standardizeErrorKeys from "./standardizeErrorKeys";
import sessionManager from "./sessionManager";
import extend from "lodash/extend";
import isEmpty from "lodash/isEmpty";

// These are the actual chainable responses that can be used after calling request(dispatcher).respond()
let defs = {

    // Checks for http errors *and* makes sure that res.body.success is set to true
    onError(err, res, next, callback) {
        let self = this;
        defs.onHttpError().fn.call(this, err, res,
            function() {
                if (res.body && res.body.success) {
                    return next();
                } else {
                    if (self.showingLoadSpinner) {
                        self.dispatcher.emit("HIDE_LOAD_SPINNER");
                    }
                    return callback && callback(err, res, next);
                }
            },
            // can't just pass callback directly, because then the above next
            // handler will get called if there is an http error, rather than the
            // true "next" that was passed into onError
            function() {
                callback && callback(err, res, next);
            });
    },

    onHttpError(err, res, next, callback) {
        // api request is unauthorized, session must be lost...
        if (res && res.unauthorized) {
            // TODO not set in Mobx land, will default to /login
            let loginUrl = sessionManager.getLoginPath();
            if (window && window.location.toString().indexOf(loginUrl) == -1) { // prevent redirect loop
                window.location.assign("/logout?redirectUrl=" + loginUrl + "&errorCode=4");
                return;
            }
        }

        // log any http errors and let the user do their own callback too
        if (err || (res && res.status != 200)) {
            if (err && err.message) {
                clientErrorLogger.notify(err.message, "error");
            } else {
                clientErrorLogger.notify(`Received ${res.status} from ${res.url}`, "error");
            }
            if (this.showingLoadSpinner) {
                this.dispatcher.emit("HIDE_LOAD_SPINNER");
            }
            callback && callback(err, res, next);
            return;
        }

        next();
    },

    // check for standard validation error response
    onFormError(err, res, next, formPath, callback) {
        if (!err && res.body && !res.body.success) {
            if (!isEmpty(res.body.errors)) {
                let errors = res.body.errors;
                errors = standardizeErrorKeys(errors);
                this.dispatcher.emit("SET_SERVER_ERRORS", { errors, formPath });
                if (this.showingLoadSpinner) {
                    this.dispatcher.emit("HIDE_LOAD_SPINNER");
                }
                callback && callback(err, res, next);
                return;
            }
        }

        next();
    },

    // check for a standard redirect response
    onRedirect(err, res, next) {
        if (!err && res && res.body && res.body.redirectUrl) {
            if (window.location.pathname + window.location.hash === res.body.redirectUrl) {
                window.location.reload(true);
            } else {
                window.location.assign(res.body.redirectUrl);
            }
            return;
        }

        next();
    }
};

let wrapFunction = function(responseFn) {
    return function() {
        let context = extend({ __responseContext: true, fn: responseFn, args: arguments }, defs);
        this.next = context;
        context.root = this.root;
        return context;
    };
};

// Wrap all of the response functions so that they are chainable.
Object.keys(defs).forEach(key => defs[key] = wrapFunction(defs[key]));

// Holy toledo, recursion! This just works its way down the chained functions.
let chain = function(next, err, res) {
    if (next) {
        let self = this;
        // The anonymous function is for calling the next function in the chain, which is used optionally so that
        // handlers can choose whether or not to short-circuit.
        let internalArgs = [err, res, () => chain.call(self, next.next, err, res)];
        // Get the user-defined args.
        let nextArgs = Array.prototype.slice.apply(next.args || []);
        next.fn.apply(this, internalArgs.concat(nextArgs));
    }
};

// This must be the last method in a request(dispatcher).respond() chain, because it is responsible
// for telling superagent to actually make the AJAX request.
defs.handle = function(callback) {
    if (!this.__responseContext) {
        throw new Error("Invalid response chain");
    }

    let self = this;
    let root = this.root;
    if (root.aborted) {
        if (root.showingLoadSpinner) {
            root.dispatcher.emit("HIDE_LOAD_SPINNER");
        }
        // By returning before calling req.end(), the AJAX request will never be made.
        return;
    }

    root.req.end(function(err, res) {
        // This method will only get called if all of the chained methods succeed
        let responseFn = function() {
            if (root.showingLoadSpinner) {
                root.dispatcher.emit("HIDE_LOAD_SPINNER");
            }
            callback && callback(err, res);
        };

        let context = extend({ __responseContext: true, fn: responseFn, callback }, defs);
        self.next = context;
        root.fn(err, res);
    });
};

// Returned by request(dispatcher).respond() -- this is the root of the response chain.
export default function responseHandler(req, wrapper) {
    let context = extend({ __responseContext: true, req }, defs);
    context.root = context;
    context.aborted = wrapper.aborted;
    context.showingLoadSpinner = wrapper.showingLoadSpinner;
    context.dispatcher = wrapper.dispatcher;
    context.fn = function(err, res) {
        chain.call({
            dispatcher: wrapper.dispatcher,
            showingLoadSpinner: wrapper.showingLoadSpinner
        }, context.next, err, res);
    };
    return context;
}